import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import type { RootState, AppDispatch } from "@/app/store";

import { DISPERSIONS_CACHE_DURATION } from "../constants/dispersions.constants";
import {
  clearQuote,
  setQuoteExpired,
  updateFormField,
  updateRecipientField,
  clearRecipientData,
  setCurrentStep,
  resetForm,
  restartFormWith,
} from "../store/dispersionsSlice";
import {
  thunkFetchCountries,
  thunkFetchRuntimeWallet,
  thunkFetchWalletList,
  thunkFetchVerifiedClientIds,
  thunkFetchQuote,
} from "../store/dispersionsThunks";
import type { DispersionFormData, DispersionQuoteParams } from "../types/dispersions-api.types";

export const useDispersions = () => {
  const dispatch = useDispatch<AppDispatch>();

  // Selectors
  const countries = useSelector((state: RootState) => state.dispersions.countries.countries);
  const countriesLoading = useSelector((state: RootState) => state.dispersions.countries.isLoading);
  const countriesError = useSelector((state: RootState) => state.dispersions.countries.error);
  const lastFetched = useSelector((state: RootState) => state.dispersions.countries.lastFetched);

  const runtimeWalletsByCurrency = useSelector(
    (state: RootState) => state.dispersions.runtimeWallets.walletsByCurrency,
  );
  const runtimeWalletsLoading = useSelector(
    (state: RootState) => state.dispersions.runtimeWallets.loadingCurrencies,
  );
  const runtimeWalletsErrors = useSelector((state: RootState) => state.dispersions.runtimeWallets.errors);
  const availableWalletCurrencies = useSelector(
    (state: RootState) => state.dispersions.runtimeWallets.availableCurrencies,
  );
  const isWalletListLoading = useSelector(
    (state: RootState) => state.dispersions.runtimeWallets.isListLoading,
  );
  const hasWalletsAccess = useSelector((state: RootState) => state.dispersions.runtimeWallets.hasAccess);
  const walletListFetchedAt = useSelector((state: RootState) => state.dispersions.runtimeWallets.lastFetched);
  // Answered (or denied for good): only then can "the organization has no such wallet" be told apart from
  // "the list has not arrived yet".
  const isWalletListSettled = !isWalletListLoading && (walletListFetchedAt !== null || !hasWalletsAccess);

  const verifiedClientIds = useSelector((state: RootState) => state.dispersions.clientKyc.verifiedClientIds);
  const clientKycLoading = useSelector((state: RootState) => state.dispersions.clientKyc.isLoading);
  const clientKycError = useSelector((state: RootState) => state.dispersions.clientKyc.error);

  const quoteData = useSelector((state: RootState) => state.dispersions.quote.data);
  const quoteLoading = useSelector((state: RootState) => state.dispersions.quote.isLoading);
  const quoteError = useSelector((state: RootState) => state.dispersions.quote.error);
  const quoteErrorCode = useSelector((state: RootState) => state.dispersions.quote.errorCode);
  const lastChangedAmountField = useSelector(
    (state: RootState) => state.dispersions.quote.lastChangedAmountField,
  );
  const quoteExpired = useSelector((state: RootState) => state.dispersions.quote.isExpired);

  const currentStep = useSelector((state: RootState) => state.dispersions.form.currentStep);
  const formData = useSelector((state: RootState) => state.dispersions.form.formData);
  const isSubmitting = useSelector((state: RootState) => state.dispersions.form.isSubmitting);
  const submitError = useSelector((state: RootState) => state.dispersions.form.submitError);

  // Actions
  const fetchCountries = useCallback(
    (force: boolean = false) => {
      const shouldFetch = force || !lastFetched || Date.now() - lastFetched > DISPERSIONS_CACHE_DURATION;

      if (shouldFetch) {
        dispatch(thunkFetchCountries());
      }
    },
    [dispatch, lastFetched],
  );

  // Returns the thunk's promise so a caller can act on the OUTCOME. The re-quote modal used to `await` this
  // and close itself regardless, because a block body swallowed the return and the thunk resolves on failure
  // too — leaving an expired quote on screen with nothing left to reopen the modal.
  const fetchQuote = useCallback(
    (params: DispersionQuoteParams) => dispatch(thunkFetchQuote(params)),
    [dispatch],
  );

  type DispersionFormField = Exclude<keyof DispersionFormData, "recipientFields">;

  const updateField = useCallback(
    <K extends DispersionFormField>(field: K, value: DispersionFormData[K]) => {
      dispatch(updateFormField({ field, value }));
    },
    [dispatch],
  );

  const updateRecipient = useCallback(
    (field: string, value: unknown) => {
      dispatch(updateRecipientField({ field, value }));
    },
    [dispatch],
  );

  // Full recipient reset for corridor changes: typed fields + client/account selection + recipient mode.
  const clearRecipientDataForCorridorChange = useCallback(() => {
    dispatch(clearRecipientData());
  }, [dispatch]);

  const clearQuoteData = useCallback(() => {
    dispatch(clearQuote());
  }, [dispatch]);

  const markQuoteExpired = useCallback(() => {
    dispatch(setQuoteExpired(true));
  }, [dispatch]);

  const goToStep = useCallback(
    (step: 1 | 2 | 3) => {
      dispatch(setCurrentStep(step));
    },
    [dispatch],
  );

  const resetDispersionForm = useCallback(() => {
    dispatch(resetForm());
  }, [dispatch]);

  /** Reset the wizard and seed the next one — the receipt's two repeat actions. */
  const restartDispersionForm = useCallback(
    (seed: Partial<DispersionFormData>) => {
      dispatch(restartFormWith(seed));
    },
    [dispatch],
  );

  // Auto-fetch countries on mount
  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  // ── Runtime (ledger) wallets — the origin of every dispersion, keyed by currency ──
  const fetchRuntimeWallet = useCallback(
    (currency: string) => {
      dispatch(thunkFetchRuntimeWallet(currency));
    },
    [dispatch],
  );

  /** Loads every activated wallet (cached 5 min) — the origin selector's options. */
  const fetchWalletList = useCallback((force = false) => dispatch(thunkFetchWalletList(force)), [dispatch]);

  /** `undefined` = never fetched; `null` = fetched, org has no wallet in that currency. */
  const getRuntimeWalletForCurrency = useCallback(
    (currency: string) => {
      return runtimeWalletsByCurrency[currency];
    },
    [runtimeWalletsByCurrency],
  );

  const isRuntimeWalletLoading = useCallback(
    (currency: string) => runtimeWalletsLoading.includes(currency),
    [runtimeWalletsLoading],
  );

  const getRuntimeWalletError = useCallback(
    (currency: string) => runtimeWalletsErrors[currency] || null,
    [runtimeWalletsErrors],
  );

  // Cached in the thunk: safe to call from every recipient panel on mount.
  const fetchVerifiedClientIds = useCallback(() => {
    dispatch(thunkFetchVerifiedClientIds());
  }, [dispatch]);

  return {
    countries,
    countriesLoading,
    countriesError,
    fetchCountries,

    fetchRuntimeWallet,
    fetchWalletList,
    runtimeWalletsByCurrency,
    availableWalletCurrencies,
    isWalletListLoading,
    isWalletListSettled,
    hasWalletsAccess,
    getRuntimeWalletForCurrency,
    isRuntimeWalletLoading,
    getRuntimeWalletError,

    verifiedClientIds,
    clientKycLoading,
    clientKycError,
    fetchVerifiedClientIds,

    quoteData,
    quoteLoading,
    quoteError,
    quoteErrorCode,
    quoteExpired,
    lastChangedAmountField,
    fetchQuote,
    markQuoteExpired,

    currentStep,
    formData,
    isSubmitting,
    submitError,
    updateField,
    updateRecipient,
    clearRecipientDataForCorridorChange,
    clearQuoteData,
    goToStep,
    resetDispersionForm,
    restartDispersionForm,
  };
};
